/**
 * Custom Carousel Accessibility
 *
 * Manages tabindex and aria-hidden on product cards based on visibility within carousels.
 * - Visible product cards: aria-hidden="false", tabindex="0" on interactive children
 * - Non-visible product cards: aria-hidden="true", tabindex="-1" on interactive children
 *
 * Handles both static and dynamically loaded carousels (e.g., product-recommendations).
 *
 * Non-visible slides are hidden using aria-hidden="true" on each card wrapper and tabindex="-1" on interactive children
 * to prevent screen readers from accessing visually hidden content (including product images).
 * Original tabindex values are kept in data-original-tabindex and restored when a card comes back into view.
 */

/**
 * Handles carousels with arrow button navigation (prev/next buttons)
 * Manages aria-hidden and tabindex on product card wrappers and their focusable elements.
 */
class CarouselArrowAccessibility {
  constructor() {
    this.init();
  }

  async init() {
    try {
      await Promise.all([
        customElements.whenDefined('carousel-prev-button'),
        customElements.whenDefined('carousel-next-button')
      ]);
      
      this.setupExistingButtons();
      
      const observer = new MutationObserver(() => this.setupExistingButtons());
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (error) {
      console.error('[CarouselArrows] Initialization error:', error);
    }
  }

  setupExistingButtons() {
    document.querySelectorAll('[is="carousel-prev-button"], [is="carousel-next-button"]').forEach((button) => {
      if (!button._carouselAccessibilitySetup) {
        button._carouselAccessibilitySetup = true;
        this.setupButton(button);
      }
    });
  }

  setupButton(button) {
    const checkCarousel = () => {
      if (!button.carousel) {
        setTimeout(checkCarousel, 50);
        return;
      }

      // If the carousel is inside a hidden panel, defer setup until the panel is shown
      const hiddenPanel = button.carousel.closest('[hidden]');
      if (hiddenPanel) {
        const observer = new MutationObserver((mutations, obs) => {
          if (!hiddenPanel.hasAttribute('hidden')) {
            obs.disconnect();
            this.initCarouselTabindex(button);
          }
        });
        observer.observe(hiddenPanel, { attributes: true, attributeFilter: ['hidden'] });
        return;
      }

      this.initCarouselTabindex(button);
    };

    checkCarousel();
  }

  initCarouselTabindex(button) {
    // Set initial tabindex - wait for carousel to have dimensions
    requestAnimationFrame(() => {
      if (button.carousel.getBoundingClientRect().width > 0) {
        this.updateProductTabIndex(button);
      } else {
        // Carousel not yet laid out (e.g., freshly unhidden panel) — wait for resize
        const resizeObserver = new ResizeObserver((entries) => {
          if (entries[0].contentRect.width > 0) {
            resizeObserver.disconnect();
            this.updateProductTabIndex(button);
          }
        });
        resizeObserver.observe(button.carousel);
      }
    });

    let scrollTimeout;
    button.carousel.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => this.updateProductTabIndex(button), 150);
      },
      { passive: true }
    );

    // Listen for carousel lifecycle events (arrow-driven or non-scrolling navigations)
    button.carousel.addEventListener('carousel:change', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.updateProductTabIndex(button), 150);
    });
    button.carousel.addEventListener('carousel:settle', () => this.updateProductTabIndex(button));

    // Watch for dynamic content loading (e.g., product-recommendations, recently-viewed)
    const observer = new MutationObserver(() => this.updateProductTabIndex(button));
    observer.observe(button.carousel, { childList: true, subtree: true });
  }

  updateProductTabIndex(button) {
    if (!button.carousel) return;

    const carouselRect = button.carousel.getBoundingClientRect();
    const productCards = button.carousel.querySelectorAll('.product-card');

    // For non-product carousels (e.g., sidebar promo), fall back to [role="group"] slides
    if (productCards.length === 0) {
      this.updateGenericSlides(button.carousel, carouselRect);
      return;
    }

    const activeEl = document.activeElement;
    let focusTargetNeeded = false;
    let firstVisibleInteractive = null;

    // Partially visible cards count as hidden; the 1px tolerance absorbs sub-pixel rounding.
    productCards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      const isVisible =
        cardRect.left >= carouselRect.left - 1 && cardRect.right <= carouselRect.right + 1;

      if (!isVisible && card.contains(activeEl)) {
        focusTargetNeeded = true;
      }

      card.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      if (isVisible) {
        card.removeAttribute('tabindex');
      } else {
        card.setAttribute('tabindex', '-1');
      }

      const focusableElements = card.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex], [contenteditable="true"], ' +
          'audio[controls], video[controls], details > summary'
      );

      focusableElements.forEach((element) => {
        if (isVisible) {
          if (element.hasAttribute('data-original-tabindex')) {
            const originalTabindex = element.getAttribute('data-original-tabindex');
            if (originalTabindex === '') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalTabindex);
            }
            element.removeAttribute('data-original-tabindex');
          }
        } else {
          if (!element.hasAttribute('data-original-tabindex')) {
            const currentTabindex = element.getAttribute('tabindex');
            element.setAttribute('data-original-tabindex', currentTabindex !== null ? currentTabindex : '');
          }
          element.setAttribute('tabindex', '-1');
        }

        if (element.classList.contains('color-swatch') && !element._hasKeyHandler) {
          element._hasKeyHandler = true;
          element.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              element.click();
            }
          });
        }
      });

      // Track first visible interactive element for focus recovery
      if (isVisible && !firstVisibleInteractive) {
        firstVisibleInteractive = card.querySelector('a[href], button:not([disabled])');
      }
    });

    if (focusTargetNeeded && firstVisibleInteractive) {
      firstVisibleInteractive.focus();
    }
  }

  updateGenericSlides(carousel, carouselRect) {
    const slides = carousel.querySelectorAll('[role="group"]');
    if (slides.length === 0) return;

    const activeEl = document.activeElement;
    let focusTargetNeeded = false;
    let firstVisibleInteractive = null;

    slides.forEach((slide) => {
      const slideRect = slide.getBoundingClientRect();
      const isVisible = slideRect.right > carouselRect.left + 10 && slideRect.left < carouselRect.right - 10;

      if (!isVisible && slide.contains(activeEl)) {
        focusTargetNeeded = true;
      }

      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

      const focusableElements = slide.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex], [contenteditable="true"], ' +
          'audio[controls], video[controls], details > summary'
      );

      focusableElements.forEach((element) => {
        if (isVisible) {
          if (element.hasAttribute('data-original-tabindex')) {
            const originalTabindex = element.getAttribute('data-original-tabindex');
            if (originalTabindex === '') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalTabindex);
            }
            element.removeAttribute('data-original-tabindex');
          }
        } else {
          if (!element.hasAttribute('data-original-tabindex')) {
            const currentTabindex = element.getAttribute('tabindex');
            element.setAttribute('data-original-tabindex', currentTabindex !== null ? currentTabindex : '');
          }
          element.setAttribute('tabindex', '-1');
        }
      });

      if (isVisible && !firstVisibleInteractive && focusableElements.length > 0) {
        firstVisibleInteractive = focusableElements[0];
      }
    });

    if (focusTargetNeeded && firstVisibleInteractive) {
      firstVisibleInteractive.focus();
    }
  }
}

/**
 * Handles carousels with page dot navigation (carousel-navigation)
 * Manages aria-hidden and tabindex on all slides and their focusable elements.
 */
class CarouselPageDotAccessibility {
  constructor() {
    this.init();
  }

  async init() {
    try {
      await customElements.whenDefined('carousel-navigation');
      
      this.setupExistingNavigations();
      
      const observer = new MutationObserver(() => this.setupExistingNavigations());
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (error) {
      console.error('[CarouselPageDots] Initialization error:', error);
    }
  }

  setupExistingNavigations() {
    document.querySelectorAll('carousel-navigation').forEach((nav) => {
      if (!nav._carouselAccessibilitySetup) {
        nav._carouselAccessibilitySetup = true;
        this.setupNavigation(nav);
      }
    });
  }

  setupNavigation(nav) {
    const carouselId = nav.getAttribute('aria-controls');
    if (!carouselId) return;

    const checkCarousel = () => {
      const carousel = document.getElementById(carouselId);
      if (!carousel) {
        setTimeout(checkCarousel, 50);
        return;
      }

      this.setupRovingTabindex(nav, carousel);

      requestAnimationFrame(() => {
        if (carousel.getBoundingClientRect().width > 0) {
          this.updateCarouselSlides(carousel);
        } else {
          const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0].contentRect.width > 0) {
              resizeObserver.disconnect();
              this.updateCarouselSlides(carousel);
            }
          });
          resizeObserver.observe(carousel);
        }
      });

      let scrollTimeout;
      carousel.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => this.updateCarouselSlides(carousel), 150);
        },
        { passive: true }
      );

      carousel.addEventListener('carousel:change', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => this.updateCarouselSlides(carousel), 150);
      });
      carousel.addEventListener('carousel:settle', () => this.updateCarouselSlides(carousel));

      const observer = new MutationObserver(() => this.updateCarouselSlides(carousel));
      observer.observe(carousel, { childList: true, subtree: true });
    };

    checkCarousel();
  }

  setupRovingTabindex(nav, carousel) {
    const buttons = Array.from(nav.querySelectorAll('button'));
    const isTablist = nav.getAttribute('role') === 'tablist';

    if (isTablist) {
      this.updateDotTabindex(buttons);

      carousel.addEventListener('carousel:change', (event) => {
        this.updateDotTabindex(buttons, event.detail.index);
      });
    }

    // Arrow key navigation between dots
    buttons.forEach((button, index) => {
      button.addEventListener('keydown', (e) => {
        let targetIndex = -1;

        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          targetIndex = index > 0 ? index - 1 : buttons.length - 1;
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          targetIndex = index < buttons.length - 1 ? index + 1 : 0;
        } else if (e.key === 'Home') {
          e.preventDefault();
          targetIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          targetIndex = buttons.length - 1;
        }

        if (targetIndex !== -1) {
          buttons[targetIndex].click();
          buttons[targetIndex].focus();
        }
      });
    });
  }

  updateDotTabindex(buttons, selectedIndex = null) {
    buttons.forEach((button, index) => {
      const isSelected = selectedIndex !== null 
        ? index === selectedIndex 
        : button.getAttribute('aria-current') === 'true';
      button.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
  }

  updateCarouselSlides(carousel) {
    const carouselRect = carousel.getBoundingClientRect();
    const slides = carousel.children;

    const activeEl = document.activeElement;
    let focusTargetNeeded = false;
    let firstVisibleInteractive = null;

    Array.from(slides).forEach(slide => {
      const slideRect = slide.getBoundingClientRect();
      const isVisible = slideRect.right > carouselRect.left && slideRect.left < carouselRect.right;

      if (!isVisible && slide.contains(activeEl)) {
        focusTargetNeeded = true;
      }

      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

      const focusableElements = slide.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex], [contenteditable="true"], ' +
          'audio[controls], video[controls], details > summary'
      );

      focusableElements.forEach((element) => {
        // Nested <scroll-carousel> focusables belong to CarouselArrowAccessibility's per-card pass; a
        // blanket restore here would undo its hiding of off-screen cards.
        if (isVisible && element.closest('scroll-carousel')) {
          return;
        }

        if (isVisible) {
          if (element.hasAttribute('data-original-tabindex')) {
            const originalTabindex = element.getAttribute('data-original-tabindex');
            if (originalTabindex === '') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalTabindex);
            }
            element.removeAttribute('data-original-tabindex');
          }
        } else {
          if (!element.hasAttribute('data-original-tabindex')) {
            const currentTabindex = element.getAttribute('tabindex');
            element.setAttribute('data-original-tabindex', currentTabindex !== null ? currentTabindex : '');
          }
          element.setAttribute('tabindex', '-1');
        }
      });

      // The arrow pass for inner carousels ran while this slide was hidden at zero width; make it
      // recompute now that the slide is laid out.
      if (isVisible) {
        slide.querySelectorAll('scroll-carousel').forEach((inner) => {
          inner.dispatchEvent(new CustomEvent('carousel:settle'));
        });
      }

      if (isVisible && !firstVisibleInteractive && focusableElements.length > 0) {
        firstVisibleInteractive = focusableElements[0];
      }
    });

    if (focusTargetNeeded && firstVisibleInteractive) {
      firstVisibleInteractive.focus();
    }
  }
}

new CarouselArrowAccessibility();
new CarouselPageDotAccessibility();
